import React, {FunctionComponent, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState} from "react";
import useQuery from "../../../../../../../hooks/use-query";
import {useAppDispatch} from "../../../../../../../redux";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import Api from "../../../../../../../../core/services/api/api";
import DateUtils, {DateUtilsTypes} from "../../../../../../../../core/services/date-utils";
import {AugmentedDashboardWidgetEntry, DashboardWidgetEntry} from "../../types";
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    IconButtonProps,
    ListItemIcon,
    listItemIconClasses,
    ListItemText,
    MenuItem,
    MenuItemProps,
    Popover,
    Stack,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {EditOutlined, InfoOutlined, MoreVertRounded, SettingsOutlined, TuneRounded} from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import {DashboardWidgetConfig} from "../../dashboard-widget.config";
// @ts-ignore
import {ReactComponent as Excel} from "../../assets/excel.svg";
import useResizeObserver, {type Rect} from "../../../../../../../../packages/data-grid/ui/hooks/use-resize-observer";
import useCombinedRefs from "../../../../../../../../packages/data-grid/ui/hooks/use-combined-refs";
import UIUtils from "../../../../../../../utils";
import {useTranslation} from "react-i18next";


export interface DashboardWidgetHeaderProps {
    widget?: AugmentedDashboardWidgetEntry;
    config: DashboardWidgetConfig;
    onDelete?: (id: DashboardWidgetEntry['connectionId']) => void;
    lastFetched?: DateUtilsTypes.Instance | null;
    isEditMode?: boolean;
    hasNoConfig: boolean;
    simplified?: boolean;
    openEditWidgetFiltersDialog: () => void;
    canViewWidgetFilters?: boolean;
    canManageWidgetFilters?: boolean;
    isLoading: boolean;
    exportData?: (() => Promise<void>);
    isExporting?: boolean;
    hasNavigation?: boolean;
    toggleViewSettings?: () => void;
    refresh: () => void;
    openEditWidgetTitleDialog: () => void;
}

export const DashboardWidgetHeader: React.FC<DashboardWidgetHeaderProps> = ({
                                                                                widget,
                                                                                config,
                                                                                onDelete,
                                                                                lastFetched,
                                                                                isEditMode,
                                                                                hasNoConfig,
                                                                                simplified,
                                                                                openEditWidgetFiltersDialog,
                                                                                canViewWidgetFilters,
                                                                                canManageWidgetFilters,
                                                                                isLoading,
                                                                                exportData,
                                                                                isExporting,
                                                                                hasNavigation,
                                                                                toggleViewSettings,
                                                                                refresh,
                                                                                openEditWidgetTitleDialog,
                                                                            }) => {
    const {rect, ref: resizeObserverRef} = useResizeObserver();
    const containerRef = useRef<HTMLTableElement>(null);
    const ref = useCombinedRefs(resizeObserverRef, containerRef);

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 1,}} ref={ref}>
                <Grid2 container spacing={1} alignItems="flex-start" justifyContent={'space-between'} width={'100%'} m={0}>
                    <Grid2 xs>
                        <Box sx={{display: 'flex', flexDirection: 'column'}}>
                            <Box sx={{display: 'flex', gap: 0.5}}>
                                {
                                    isEditMode && !simplified &&
                                    <IconButton
                                        size={'small'}
                                        color="primary"
                                        component={'span'}
                                        sx={{marginInlineEnd: 0.5}}
                                        onClick={() => openEditWidgetTitleDialog()}
                                    >
                                        <EditOutlined/>
                                    </IconButton>
                                }
                                <Tooltip disableInteractive title={
                                    <Typography variant="body2" fontWeight={600}>
                                        {widget?.customTitle ?? widget?.title ?? ""}
                                    </Typography>
                                }>
                                    <Typography variant="body1" fontWeight={600} sx={{textOverflow: "ellipsis", overflow: 'hidden', whiteSpace: 'nowrap'}}>
                                        {widget?.customTitle ?? widget?.title ?? ""}
                                    </Typography>
                                </Tooltip>
                                <Tooltip
                                    slotProps={{
                                        tooltip: {
                                            sx: {maxWidth: 500, minWidth: 'min(500px, 90vw)', p: 0},
                                        },
                                    }}
                                    title={
                                        <Box sx={{borderRadius: 1, maxHeight: 300, overflow: 'auto', position: 'relative'}}>
                                            <Stack direction={'column'} spacing={1} sx={{padding: 1}}>
                                                <Stack direction={'column'} spacing={1} sx={{
                                                    position: 'sticky',
                                                    top: 0,
                                                    backgroundColor: theme => `rgba(${theme.vars.palette.Tooltip.bg.replace("rgba(", "rgba(").replace("0.92)", "")}, 1)`,
                                                    backdropFilter: 'blur(10px)',
                                                    zIndex: 1,
                                                }}>
                                                    <Typography variant={'body1'} fontWeight={600}>{widget?.customTitle ?? widget?.title ?? ""}</Typography>
                                                    <Typography variant="body2">
                                                        <Chip component={'span'} size={'small'} variant={'filled'} color={'primary'} label={"Category"}/> <Typography
                                                        component={'span'}
                                                        variant="body2">
                                                        {widget?.category}
                                                    </Typography>
                                                    </Typography>
                                                    <Typography variant={'body2'}>
                                                        <Chip component={'span'} size={'small'} variant={'filled'} color={'primary'} label={"Description"}/> <Typography
                                                        component={'span'}
                                                        variant="body2">
                                                        {widget?.description ?? "No description available."}
                                                    </Typography>
                                                    </Typography>
                                                    <Divider/>
                                                </Stack>
                                                <Typography variant={'caption'} component={'div'}>
                                                    <Chip component={'span'} size={'small'} variant={'filled'} color={'secondary'} label={"Parameters"}/>
                                                    <table style={{marginBlockStart: 16, width: '100%'}}>
                                                        <thead>
                                                        <tr>
                                                            <th align="left">Title</th>
                                                            <th align="left">Description</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {widget?.parameters?.map((param, index) => (
                                                            <tr key={index}>
                                                                <td align="left">
                                                                    <Typography variant="caption" fontWeight={600}>
                                                                        {param.title}
                                                                    </Typography>
                                                                </td>
                                                                <td align="left">
                                                                    <Typography variant="caption">
                                                                        {param.description}
                                                                    </Typography>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    }
                                >
                                    <InfoOutlined color={'secondary'}/>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Grid2>
                    <Grid2 xs={'auto'} display={'flex'} justifyContent={'flex-end'}>
                        <DashboardWidgetActions
                            rect={rect}
                            widget={widget}
                            config={config}
                            onDelete={onDelete}
                            lastFetched={lastFetched}
                            isEditMode={isEditMode}
                            hasNoConfig={hasNoConfig}
                            simplified={simplified}
                            openEditWidgetFiltersDialog={openEditWidgetFiltersDialog}
                            canViewWidgetFilters={canViewWidgetFilters}
                            canManageWidgetFilters={canManageWidgetFilters}
                            isLoading={isLoading}
                            exportData={exportData}
                            isExporting={isExporting}
                            hasNavigation={hasNavigation}
                            toggleViewSettings={toggleViewSettings}
                            refresh={refresh}
                        />
                    </Grid2>
                    {
                        !simplified &&
                        <Typography variant="caption" sx={{marginBlockStart: -0.5, width: '100%', marginInlineStart: 0.5}}>
                            Category: <Typography component={'span'} variant="caption" color={'tertiary.main'}>{widget?.category}</Typography>
                        </Typography>
                    }
                </Grid2>
            </Box>
        </>
    );
}

export interface DashboardWidgetActionsProps extends Omit<DashboardWidgetHeaderProps, 'openEditWidgetTitleDialog'> {
    rect?: Rect;
}

export const DashboardWidgetActions: React.FC<DashboardWidgetActionsProps> = ({
                                                                                  rect,
                                                                                  widget,
                                                                                  config,
                                                                                  onDelete,
                                                                                  lastFetched,
                                                                                  isEditMode,
                                                                                  hasNoConfig,
                                                                                  simplified,
                                                                                  openEditWidgetFiltersDialog,
                                                                                  canViewWidgetFilters,
                                                                                  canManageWidgetFilters,
                                                                                  isLoading,
                                                                                  exportData,
                                                                                  isExporting,
                                                                                  hasNavigation,
                                                                                  toggleViewSettings,
                                                                                  refresh,
                                                                              }) => {
    const [, setQuery] = useQuery();
    const [removing, setRemoving] = useState(false);
    const dispatch = useAppDispatch();
    const isMounted = useIsMounted();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2.widget-header'});
    const showAsDots = (rect?.width ?? 0) < 300;
    const [anchorEl, setAnchorEl] = useState<HTMLElement>();
    const open = useMemo(() => Boolean(anchorEl), [anchorEl]);


    /**
     * Handle widget deletion
     */
    const handleDelete = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `Widget: "${widget?.customTitle ?? widget?.title ?? ""}"`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return true;
                setRemoving(true);
                const response = await Api.removeCustomDashboardWidget(widget?.connectionId);
                if (!isMounted())
                    return true;
                setRemoving(false);
                if (response?.resultFlag) {
                    onDelete?.(widget?.connectionId as string);
                }
                return response?.resultFlag ?? false;
            }
        }));
    }, [dispatch, widget?.customTitle, widget?.title, widget?.connectionId, isMounted, onDelete]);

    /**
     * Handle log navigation
     */
    const handleLogNavigate = useCallback(() => config.navigateToAppropriatePage(setQuery), [config, setQuery]);

    /**
     * Format last refreshed time
     */
    const formattedLastRefreshed = useMemo(() => {
        return lastFetched ? DateUtils.local(lastFetched).fromNow() : 'never';
    }, [lastFetched]);


    /**
     * Opens the menu.
     * @param {MouseEvent<HTMLElement>} event    The event.
     */
    const openMenu = useCallback<MouseEventHandler<HTMLElement>>((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    /**
     * Closes the menu.
     */
    const closeMenu = useCallback<VoidCallback>(() => {
        setAnchorEl(undefined);
    }, []);
    /**
     * Closes the menu and calls the [action] callback.
     *
     * @param {MouseEventHandler<HTMLElement>} action    The action callback.
     * @return {MouseEventHandler<HTMLElement>}          The event handler.
     */
    const closeMenuOnActionSelection = useCallback<(action?: MouseEventHandler<HTMLElement>) => MouseEventHandler<HTMLElement>>(
        (action) => (...args) => {
            closeMenu();
            action?.(...args);
        }, [closeMenu])

    /**
     * Effect to close the menu when showAsDots changes to false
     */
    useEffect(() => {
        if (!showAsDots)
            closeMenu();
    }, [showAsDots, closeMenu]);

    const actions = useMemo(() => {
        const canShowWidgetFilters = Boolean(canViewWidgetFilters || canManageWidgetFilters);
        const actions = [
            {
                key: 'edit-filters',
                title: canManageWidgetFilters ? t(t => t.manageAppliedFilters) : t(t => t.viewAppliedFilters),
                icon: <TuneRounded fontSize="small"/>,
                color: 'primary',
                onClick: () => {
                    openEditWidgetFiltersDialog();
                },
                sx: {
                    ...(!!widget?.filters && {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        [`.${listItemIconClasses.root}`]: {
                            color: 'primary.contrastText',
                        },
                        '&:hover': {
                            backgroundColor: 'primary.dark',
                        },
                    })
                },
                disabled: isLoading,
                show: !isEditMode && !hasNoConfig && !simplified && canShowWidgetFilters,
            },
            {
                key: 'refresh',
                title: `Last refreshed ${formattedLastRefreshed}`,
                icon: <RefreshIcon fontSize="small"/>,
                color: 'primary',
                onClick: refresh,
                disabled: isLoading || removing,
                show: !isEditMode && !hasNoConfig,
            },
            {
                key: 'export',
                title: 'Export underlying data',
                icon: isExporting ? <CircularProgress size={15} color={'primary'}/> : <Excel style={{width: 20, height: 20}}/>,
                color: 'primary',
                onClick: exportData,
                disabled: isLoading || isExporting || removing,
                show: !isEditMode && !!exportData && !hasNoConfig,
            },
            {
                key: 'navigate',
                title: 'View Logs',
                color: 'primary',
                icon: <DescriptionIcon fontSize="small"/>,
                onClick: handleLogNavigate,
                disabled: removing,
                show: hasNavigation && !isEditMode && !hasNoConfig && !simplified,
            },
            {
                key: 'view-settings',
                title: 'View Settings',
                color: 'secondary',
                icon: <SettingsOutlined fontSize="small"/>,
                onClick: toggleViewSettings,
                disabled: removing,
                show: toggleViewSettings && !isEditMode && !hasNoConfig,
            },
            {
                key: 'delete',
                title: 'Delete Widget',
                color: 'error',
                icon: removing ? <CircularProgress size={15} color={'secondary'}/> : <DeleteIcon fontSize="small"/>,
                onClick: handleDelete,
                disabled: removing,
                show: isEditMode && !simplified,
            }
        ].filter(e => e.show);
        return actions.map(e => ({
            ...e,
            onClick: closeMenuOnActionSelection(e.onClick),
            mode: showAsDots && e.key !== 'delete' ? 'menu-item' as const : 'button' as const,
        }))
    }, [canManageWidgetFilters, canViewWidgetFilters, closeMenuOnActionSelection, exportData, formattedLastRefreshed, handleDelete, handleLogNavigate, hasNavigation, hasNoConfig, isEditMode, isExporting, isLoading, openEditWidgetFiltersDialog, refresh, removing, showAsDots, simplified, t, toggleViewSettings, widget?.filters])

    const hasPopoverActions = actions.some(e => e.mode === 'menu-item');
    const hasButtonActions = actions.some(e => e.mode === 'button');

    return (
        <>
            <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'flex-end'}}>
                {
                    hasPopoverActions &&
                    <>
                        <Tooltip title={`Available Actions`}>
                            <Box component={'span'}>
                                <IconButton
                                    size="small"
                                    color={'paper.primary'}
                                    onClick={openMenu}
                                >
                                    <MoreVertRounded fontSize="small"/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Popover
                            id={'panel-appbar-actions-menu'}
                            anchorEl={anchorEl}
                            open={open}
                            onClose={closeMenu}
                            onClick={closeMenu}
                            transformOrigin={{horizontal: 'right', vertical: 'top'}}
                            anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                            slotProps={{
                                paper: {sx: {backgroundImage: 'none'}}
                            }}
                        >
                            {
                                actions.filter(e => e.mode === 'menu-item').map((action) => (
                                    <DashboardWidgetAction
                                        key={action.key}
                                        title={action.title}
                                        icon={action.icon}
                                        color={action.color as any}
                                        onClick={action.onClick}
                                        disabled={action.disabled}
                                        mode={action.mode}
                                        sx={action.sx}
                                    />
                                ))
                            }
                        </Popover>
                    </>
                }
                {
                    hasButtonActions &&
                    actions.filter(e => e.mode === 'button').map((action) => (
                        <DashboardWidgetAction
                            key={action.key}
                            title={action.title}
                            icon={action.icon}
                            color={action.color as any}
                            onClick={action.onClick}
                            disabled={action.disabled}
                            mode={action.mode}
                            sx={action.sx}
                        />
                    ))
                }
            </Box>
        </>
    );
}


export interface DashboardWidgetActionProps extends Omit<IconButtonProps, 'color' | 'classes' | 'onClick'>, Omit<MenuItemProps<'button'>, 'color' | 'classes' | 'onClick'> {
    title: string,
    icon: React.ReactNode,
    mode: 'button' | 'menu-item',
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    classes?: IconButtonProps['classes'] & MenuItemProps['classes'],
    onClick?: (e: React.MouseEvent<HTMLElement>, anchorEl?: HTMLElement | null) => void,
    anchorEl?: HTMLElement | null,
}

export const DashboardWidgetAction: FunctionComponent<DashboardWidgetActionProps> = (props) => {
    const {title, icon, mode, onClick: propsOnClick, anchorEl, ...buttonProps} = props;
    const theme = useTheme();

    /**
     * The event handler for the click event.
     */
    const onClick = useCallback<MouseEventHandler<HTMLElement>>((e) => {
        if (propsOnClick) {
            propsOnClick(e, props.anchorEl);
        }
    }, [propsOnClick, props.anchorEl])


    return (
        <>
            {
                mode === 'button' && (
                    <Tooltip title={props.title}>
                        <span>
                            <IconButton
                                {...buttonProps}
                                onClick={onClick}
                                color={'paper.'.concat(props.color ?? 'primary') as 'primary'}
                            >
                                {props.icon}
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            }
            {
                mode === 'menu-item' && (
                    <MenuItem
                        {...buttonProps as MenuItemProps}
                        onClick={onClick}
                        sx={(theme) => ({
                            minHeight: `48px !important`,
                            ...(UIUtils.invokeSx<Theme>(props.sx, theme) as any),
                        })}
                    >
                        <ListItemIcon>
                            {props.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={props.title}
                            primaryTypographyProps={{
                                fontSize: theme.typography.subtitle2.fontSize,
                            }}
                        />
                    </MenuItem>
                )
            }
        </>
    );
}